import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logoGala from '../assets/common/LogoGala.png';
import FormInput from '../components/FormInput';
import OtpInput from '../components/OtpInput';
import { clearEmailCodeLink, codeRequestedHere, readEmailCodeLink } from '../utils/emailCodeLink';
import './auth.css';

// Login admin dédié (4 facteurs) : mot de passe + token secret + code OTP email
// + protection anti-bruteforce côté backend. Volontairement séparé de Login.js
// et non lié depuis l'écran de connexion grand public.
const AdminLogin = () => {
  const navigate = useNavigate();
  const { requestAdminLoginOtp, verifyOtpAndLogin } = useAuth();
  // Bouton « Me connecter » de l'e-mail : l'adresse et le code arrivent déjà saisis.
  const [link] = useState(() => {
    const l = readEmailCodeLink();
    return l.mail && l.code ? l : null;
  });
  const [step, setStep] = useState(link ? 'otp' : 'credentials'); // 'credentials' | 'otp'
  const [email, setEmail] = useState(link?.mail || '');
  const [password, setPassword] = useState('');
  const [adminToken, setAdminToken] = useState('');
  const [code, setCode] = useState(link?.code || '');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // StrictMode rejoue les effets : sans ce verrou, le code partirait deux fois.
  const autoSubmitted = useRef(false);

  const handleCredentialsSubmit = async (e) => {
    e?.preventDefault();
    if (submitting) return;
    setError('');
    setSubmitting(true);
    try {
      // Appareil de confiance encore valide : la connexion est déjà terminée,
      // aucun code n'a été envoyé — on ne doit donc pas afficher l'étape OTP.
      const { loggedIn } = await requestAdminLoginOtp(email.trim(), password, adminToken.trim());
      if (loggedIn) {
        navigate('/admin');
        return;
      }
      setCode('');
      setStep('otp');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // `typedCode` : passé par OtpInput.onComplete, l'état `code` n'étant pas encore à jour.
  const handleOtpSubmit = async (e, typedCode = code) => {
    e?.preventDefault();
    if (submitting) return;
    setError('');
    if (typedCode.length !== 6) {
      setError('Saisissez les 6 chiffres du code.');
      return;
    }
    setSubmitting(true);
    try {
      await verifyOtpAndLogin(email.trim(), typedCode);
      navigate('/admin');
    } catch (err) {
      setError(err.message);
      setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  // Ouvert depuis l'e-mail : validé d'office si ce navigateur a demandé le code.
  useEffect(() => {
    clearEmailCodeLink();
    if (link && codeRequestedHere() && !autoSubmitted.current) {
      autoSubmitted.current = true;
      handleOtpSubmit(null, link.code);
    }
    // Une seule fois, à l'ouverture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="page">
      <div className="container">
        <div className="signup-logo">
          <img src={logoGala} alt="CycleGala" className="signup-logo-image" />
        </div>

        <h2 className="signup-brand">
          <span className="signup-brand--cycle">Cycle</span>
          <span className="signup-brand--gala">Gala</span>
        </h2>

        {step === 'credentials' ? (
          <form onSubmit={handleCredentialsSubmit} noValidate>
            <p className="subtitle">Connexion administrateur</p>

            {/* Aucun placeholder sur cet écran : « admin@cyclegala.fr » souffle la
                convention de nommage du compte, et décrire le token secret en
                indique la provenance. Les libellés suffisent. */}
            <FormInput
              label="Email"
              type="email"
              name="email"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <FormInput
              label="Mot de passe"
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            {/* `off` : sans cela, un gestionnaire pourrait prendre le token pour un
                second mot de passe et l'écraser ou le proposer à la place. */}
            <FormInput
              label="Token secret"
              type="password"
              name="admin-token"
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              value={adminToken}
              onChange={(e) => setAdminToken(e.target.value)}
            />

            {error && <p className="auth-error">{error}</p>}

            <div className="button-group">
              <button type="submit" className="btn btn--primary" disabled={submitting}>
                Connexion
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleOtpSubmit} noValidate>
            <p className="subtitle">Un code à 6 chiffres a été envoyé à {email}</p>

            <OtpInput
              label="Code de connexion"
              idPrefix="admin-otp"
              value={code}
              onChange={(v) => { setCode(v); setError(''); }}
              onComplete={(v) => handleOtpSubmit(null, v)}
              disabled={submitting}
              error={!!error}
            />

            {error && <p className="auth-error">{error}</p>}
            {link && !error && !submitting && code === link.code && (
              <p className="auth-hint">Code repris de l'e-mail : touchez « Valider » pour vous connecter.</p>
            )}

            <div className="button-group">
              <button type="submit" className="btn btn--primary" disabled={submitting}>
                Valider
              </button>
            </div>

            <div className="auth-link-row">
              <button type="button" className="auth-link-btn" onClick={() => { setStep('credentials'); setError(''); }}>
                ‹ Retour
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AdminLogin;
